import { useEffect, useState } from 'react';

interface NavProps {
  theme: string;
  onToggleTheme: () => void;
}

export function Nav({ theme, onToggleTheme }: NavProps) {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('top');

  const links = [
    { href: '#top', label: 'Home' },
    { href: '#about', label: 'About' },
    { href: '#skills', label: 'Skills' },
    { href: '#exp', label: 'Path' },
    { href: '#work', label: 'Work' },
    { href: '#services', label: 'Services' },
    { href: '#blog', label: 'Log' },
  ];

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const sectionsToObserve = [...links.map(l => l.href), '#contact'];
    const sections = sectionsToObserve.map(href => document.querySelector(href)).filter(Boolean) as HTMLElement[];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-30% 0px -70% 0px' }
    );

    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <nav className="nav">
        <span className="nav-logo"><span className="dot"></span></span>
        <span className="nav-mid">
          {links.map((l) => (
            <a
              key={l.href}
              className={`nav-link ${activeSection === l.href.substring(1) ? 'active' : ''}`}
              href={l.href}
            >
              {l.label}
            </a>
          ))}
        </span>
        <a className={`nav-cta ${activeSection === 'contact' ? 'nav-cta-active' : ''}`} href="#contact">Hire →</a>
        <button
          type="button"
          className="nav-burger"
          aria-label="Open menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <span /><span /><span />
        </button>
      </nav>

      {/* Floating Theme Toggle in the bottom right corner */}
      <button
        type="button"
        className={`floating-theme-toggle ${theme}`}
        aria-label="Toggle theme"
        onClick={onToggleTheme}
        title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
      >
        <svg
          className="theme-icon"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <mask id="moon-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <circle className="moon-mask-circle" r="9" fill="black" />
          </mask>
          <circle
            className="sun-core"
            cx="12" cy="12"
            mask="url(#moon-mask)"
            fill="currentColor"
          />
          <g className="sun-rays">
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </g>
          <g className="moon-stars" fill="currentColor" stroke="none">
            <circle cx="18" cy="6" r="1.5" />
            <circle cx="21" cy="11" r="1" />
          </g>
        </svg>
      </button>

      <div className={`mobile-drawer ${open ? 'open' : ''}`} role="dialog" aria-hidden={!open}>
        <button className="mobile-close" type="button" aria-label="Close menu" onClick={() => setOpen(false)}>✕</button>
        <div className="mobile-drawer-inner">
          <div className="mobile-eyebrow">// NAVIGATION</div>
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={activeSection === l.href.substring(1) ? 'active' : ''}
              onClick={() => setOpen(false)}
            >
              {l.label}
            </a>
          ))}
          <a href="#contact" className="mobile-cta" onClick={() => setOpen(false)}>Hire me →</a>
        </div>
      </div>
    </>
  );
}
